import { queryKeys } from '@query/keys'
import { UseQueryResult, useQueries } from '@tanstack/react-query'
import { useEffect, useMemo } from 'react'
import { useOutletContext } from 'react-router-dom'

import { AnalyticsFilters } from '@pages/Trade/Analytics/components/FiltersRow'
import {
  DxtradeLatestCandlesBySymbol,
  useDxtradeLatestCandles,
} from '@pages/Trade/Markets/Instruments/hooks/useDxtradeLatestCandles'
import { initializeCurrentPrices } from '@pages/Trade/Portfolio/hooks/useCurrentPricesStore'
import {
  DxtradeInstrumentMap,
  useDxtradeInstruments,
} from '@pages/Trade/Portfolio/hooks/useDxtradeInstruments'
import { useMarketDataEvents } from '@pages/Trade/hooks/useMarketDataEvents'
import { UseTradeLoginReturn } from '@pages/Trade/hooks/useTradeLogin'

import { useApiClient } from '@utils/ApiClient'
import { ClientApiClient } from '@utils/clientApi'

import { TopAnalyticsItemDto } from '@model/AnalyticsDto'
import {
  DxTradeInstrumentDetails,
  DxTradeRawCandle,
  MarketDataEventFormats,
  MarketDataEventTypes,
} from '@model/DxTrade.types'

const MARKET_DATA_EVENT_TYPES = [
  { type: MarketDataEventTypes.QUOTE, format: MarketDataEventFormats.COMPACT },
]

export type TopAnalyticsItem = TopAnalyticsItemDto & {
  latestDailyCandle: DxTradeRawCandle | null
  instrument: DxTradeInstrumentDetails | null
}

export const useAnalyticsFetch = (filters: AnalyticsFilters) => {
  const apiClient = useApiClient(ClientApiClient)
  const { selectedAccount } = useOutletContext<UseTradeLoginReturn>()

  const results = useQueries({
    queries: [
      {
        queryKey: queryKeys.analytics.topMoversRisers(filters),
        queryFn: () => apiClient.getTopMoversRisers(filters),
        gcTime: 0,
        staleTime: 0,
        refetchInterval: 5 * 60_000,
      },
      {
        queryKey: queryKeys.analytics.topMoversFallers(filters),
        queryFn: () => apiClient.getTopMoversFallers(filters),
        gcTime: 0,
        staleTime: 0,
        refetchInterval: 5 * 60_000,
      },
      {
        queryKey: queryKeys.analytics.topTradedBought(filters),
        queryFn: () => apiClient.getTopTradedBought(filters),
        gcTime: 0,
        staleTime: 0,
        refetchInterval: 5 * 60_000,
      },
      {
        queryKey: queryKeys.analytics.topTradedSold(filters),
        queryFn: () => apiClient.getTopTradedSold(filters),
        gcTime: 0,
        staleTime: 0,
        refetchInterval: 5 * 60_000,
      },
      {
        queryKey: queryKeys.analytics.mostVolatile(filters),
        queryFn: () => apiClient.getMostVolatile(filters),
        gcTime: 0,
        staleTime: 0,
        refetchInterval: 5 * 60_000,
      },
      {
        queryKey: queryKeys.analytics.mostFollowed(filters),
        queryFn: () => apiClient.getMostFollowed(filters),
        gcTime: 0,
        staleTime: 0,
        refetchInterval: 5 * 60_000,
      },
    ] as const,
  })

  const symbols = useMemo(() => {
    const allItems = getAllAnalyticsItems(results)
    return Array.from(
      new Set(allItems.map((item) => item.instrumentSymbol).filter(Boolean))
    ).filter((x): x is string => !!x)
  }, [results])

  const accountCode = useMemo(
    () =>
      selectedAccount
        ? {
            login: selectedAccount.platformOverview?.login,
            currency: selectedAccount.currency?.id,
          }
        : undefined,
    [selectedAccount]
  )

  const { instruments } = useDxtradeInstruments(accountCode?.login ?? '', symbols)
  const latestCandles = useDxtradeLatestCandles('Day', symbols)

  const { data: marketDataEvents } = useMarketDataEvents({
    account: accountCode?.login ? `default:${accountCode.login}` : undefined,
    symbols,
    eventTypes: MARKET_DATA_EVENT_TYPES,
  })

  useEffect(() => {
    if (!marketDataEvents?.events?.length) return
    const batch: Record<string, { bid: number; ask: number }> = {}
    marketDataEvents.events.forEach((event) => {
      const { bid, ask, symbol } = event
      if (!symbol || bid == null || ask == null) return
      batch[symbol] = { bid, ask }
    })
    if (Object.keys(batch).length) {
      initializeCurrentPrices(batch)
    }
  }, [marketDataEvents])

  const [risers, fallers, bought, sold, volatile, followed] = useAnalyticsData({
    results,
    symbols,
    instruments,
    dailyCandles: latestCandles.candlesBySymbol,
  })

  return {
    risers,
    fallers,
    bought,
    sold,
    volatile,
    followed,
    symbols,
    latestCandles,
    isFetching: results.some((x) => x.isFetching),
  }
}

interface AnalyticsDataProps {
  results: UseQueryResult<TopAnalyticsItemDto[], Error>[]
  symbols: string[]
  instruments: DxtradeInstrumentMap
  dailyCandles: DxtradeLatestCandlesBySymbol
}

const useAnalyticsData = (props: AnalyticsDataProps) => {
  const { results, symbols, dailyCandles, instruments } = props

  const candleBySymbol = useMemo(() => {
    return symbols.reduce<Record<string, DxTradeRawCandle | null>>((acc, symbol) => {
      acc[symbol] = dailyCandles[symbol] ?? null
      return acc
    }, {})
  }, [symbols, dailyCandles])

  const instrumentBySymbol = useMemo(() => {
    return symbols.reduce<Record<string, DxTradeInstrumentDetails | null>>((acc, symbol) => {
      acc[symbol] = instruments[symbol] ?? null
      return acc
    }, {})
  }, [symbols, instruments])

  const withMarketData = useMemo(
    () =>
      (item: TopAnalyticsItemDto): TopAnalyticsItem => {
        const symbol = item.instrumentSymbol ?? ''
        return {
          ...item,
          latestDailyCandle: candleBySymbol[symbol] ?? null,
          instrument: instrumentBySymbol[symbol] ?? null,
        }
      },
    [candleBySymbol]
  )

  return useMemo(() => {
    return results.map((query) => {
      const { data, ...rest } = query
      return {
        ...rest,
        data: data?.map(withMarketData) ?? [],
      } as UseQueryResult<TopAnalyticsItem[], Error>
    })
  }, [results, withMarketData]) as UseQueryResult<TopAnalyticsItem[], Error>[]
}

const getAllAnalyticsItems = (
  results: UseQueryResult<TopAnalyticsItemDto[], Error>[]
): TopAnalyticsItemDto[] => {
  return results.flatMap((result) => result.data ?? [])
}
